import React, { useState, useEffect } from 'react';
import './ChampionshipPairingsModal.css';

function generateGroups(lb, allPlayers, size) {
    if (!lb || lb.length === 0) return [];

    // Sort by position ascending (leader = 1 first), then reverse (worst first)
    const sorted = [...lb].sort((a, b) => a.position - b.position);
    const reversed = sorted.reverse();

    const orderedPlayers = reversed
        .map(entry => {
            const p = allPlayers?.find(pl => pl.player_id === entry.player_id || pl.id === entry.player_id);
            return p ? { ...p, id: p.player_id || p.id, name: p.player_name || p.name, handicap: p.player_handicap || p.handicap, position: entry.position, totalScore: entry.total_stableford || entry.total_nett } : null;
        })
        .filter(Boolean);

    // Players not in leaderboard go first (worst position)
    const lbIds = new Set(orderedPlayers.map(p => p.id));
    const notInLb = (allPlayers || [])
        .filter(p => !lbIds.has(p.player_id || p.id))
        .map(p => ({ ...p, id: p.player_id || p.id, name: p.player_name || p.name, handicap: p.player_handicap || p.handicap }));
    const finalOrder = [...notInLb, ...orderedPlayers];

    const result = [];
    for (let i = 0; i < finalOrder.length; i += size) {
        const num = result.length + 1;
        result.push({ id: num, name: `Group ${num}`, players: finalOrder.slice(i, i + size) });
    }
    return result;
}

const ChampionshipPairingsModal = ({ isOpen, onClose, leaderboard, players, groupSize = 4, onEdit, presetGroups, pairingMode = 'leaderboard', onConfirm }) => {
    const [groups, setGroups] = useState([]);
    const isLeaderboardMode = pairingMode === 'leaderboard';

    // Use preset groups if provided, otherwise generate from leaderboard
    useEffect(() => {
        if (!isOpen) return;
        if (presetGroups && presetGroups.length > 0) {
            setGroups(presetGroups);
        } else if (leaderboard && leaderboard.length > 0) {
            setGroups(generateGroups(leaderboard, players, groupSize));
        }
    }, [isOpen, presetGroups, leaderboard, players, groupSize]);

    if (!isOpen) return null;

    const isFinalGroup = (idx) => isLeaderboardMode && idx === groups.length - 1 && groups.length > 1;

    const subtitle = isLeaderboardMode
        ? 'Groups based on leaderboard standings'
        : pairingMode === 'random' ? 'Randomly drawn groups'
        : pairingMode === 'mix' ? 'Drawn so everyone plays with everyone'
        : 'Manually assigned groups';

    return (
        <div className="cp-modal-overlay">
            <div className="cp-modal">
                <div className="cp-modal__header">
                    <h2>Round Groups</h2>
                    <p>{subtitle}</p>
                </div>

                {/* Groups */}
                <div className="cp-groups">
                    {groups.map((group, idx) => {
                        const isFinal = isFinalGroup(idx);
                        return (
                            <div key={group.id} className={`cp-group-card ${isFinal ? 'cp-group-card--final' : ''}`}>
                                <div className={`cp-group-card__header ${isFinal ? 'cp-group-card__header--final' : ''}`}>
                                    <span>{isFinal ? '🏆 FINAL GROUP' : group.name.toUpperCase()}</span>
                                    {isFinal && <span className="cp-group-card__header-sub">Championship pairing</span>}
                                </div>
                                <div className="cp-group-card__body">
                                    {group.players.map(p => (
                                        <div key={p.id} className={`cp-group-card__player ${isFinal ? 'cp-group-card__player--final' : ''}`}>
                                            {isLeaderboardMode && p.position && <span className="cp-group-card__player-pos">{p.position}</span>}
                                            <span className="cp-group-card__player-name">{p.name}</span>
                                            {isLeaderboardMode && p.totalScore != null && (
                                                <span className="cp-group-card__player-score">
                                                    {p.totalScore}{typeof p.totalScore === 'number' && p.totalScore > 0 ? ' pts' : ''}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Actions */}
                <div className="cp-actions">
                    {onEdit && (
                        <button className="cp-btn cp-btn--secondary" onClick={() => onEdit(groups)}>
                            Edit Groups
                        </button>
                    )}
                    {onConfirm ? (
                        <button className="cp-btn cp-btn--primary" onClick={() => onConfirm(groups)}>
                            Confirm Pairings
                        </button>
                    ) : (
                        <button className="cp-btn cp-btn--primary" onClick={onClose}>
                            Close
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChampionshipPairingsModal;
